import { z } from "zod";

export const leadSchema = z.object({
	id: z.string().optional().nullable(),
	reference_number: z.string().min(1, "Reference number is required"),
	proposal_date: z.string().min(1, "Proposal date is required"),
	project_description: z.string().min(1, "Project description is required"),
	client_name: z.string().min(1, "Client name is required"),
	amount: z.coerce.number().min(0, "Amount must be positive"),
	contact_person: z.string().optional().nullable(),
	contact_number: z.string().optional().nullable(),
	email: z.string().optional().nullable(),
	client_address: z.string().optional().nullable(),
	prepared_by: z.string().min(1, "Prepared by is required"),
	prepared_by_user: z
		.object({
			id: z.string(),
			name: z.string(),
			email: z.string(),
		})
		.optional()
		.nullable(),
	status: z.string().default("proposal_sent"),
	remarks: z.string().optional().nullable(),
	request_date: z.string().optional().nullable(),
	response_date: z.string().optional().nullable(),
	category: z.string().min(1, "Category is required"),
	client_type: z.string().optional().nullable(),
	priority: z.string().optional().nullable(),
	next_follow_up_date: z.string().optional().nullable(),
	created_at: z.string().optional().nullable(),
	updated_at: z.string().optional().nullable(),
});

export type Lead = z.infer<typeof leadSchema>;

export interface LeadStatus {
	id: string;
	name: string;
	slug: string;
	color: string;
	sort_order: number;
	is_default: boolean;
}

export const createLeadSchema = leadSchema.omit({
	id: true,
	prepared_by_user: true,
	created_at: true,
	updated_at: true,
});

export type CreateLeadData = z.infer<typeof createLeadSchema>;

export const updateLeadSchema = createLeadSchema.partial().extend({
	id: z.string(),
});

export type UpdateLeadData = z.infer<typeof updateLeadSchema>;

export interface ActivityLog {
	id: string;
	event: string;
	description: string;
	properties: {
		old?: Record<string, unknown>;
		attributes?: Record<string, unknown>;
	};
	causer: {
		id: string;
		name: string;
	} | null;
	created_at: string;
}

export interface LeadNoteAttachment {
	id: number;
	file_name: string;
	mime_type: string;
	size: number;
	human_size: string;
	url: string;
	is_image: boolean;
}

export interface LeadNote {
	id: string;
	content: string;
	created_by: {
		id: string;
		name: string;
	} | null;
	attachments: LeadNoteAttachment[];
	created_at: string;
}

export interface LeadCategory {
	id: string;
	name: string;
	short_code: string;
}

export interface LeadDashboardSummary {
	total_leads: number;
	total_pipeline_value: number;
	overdue_follow_ups: number;
	avg_response_days: number | null;
}

export interface LeadPipelineItem {
	slug: string;
	name: string;
	color: string;
	count: number;
	total_amount: number;
}

export interface LeadPriorityItem {
	priority: string;
	count: number;
	total_amount: number;
}

export interface LeadFollowUpItem {
	id: string;
	reference_number: string;
	client_name: string;
	project_description: string;
	amount: number;
	priority: string | null;
	next_follow_up_date: string;
	status: string;
	prepared_by_user: { id: string; name: string } | null;
}

export interface LeadMonthlyTrend {
	month: string;
	count: number;
	total_amount: number;
}

export interface LeadTopClient {
	client_name: string;
	lead_count: number;
	total_amount: number;
}

export interface LeadCategoryBreakdown {
	category: string;
	count: number;
	total_amount: number;
}

export interface LeadRecentActivity {
	id: string;
	event: string;
	description: string;
	lead_reference: string | null;
	lead_id: string;
	causer: { id: string; name: string } | null;
	created_at: string;
}

export interface LeadDashboard {
	summary: LeadDashboardSummary;
	pipeline: LeadPipelineItem[];
	by_priority: LeadPriorityItem[];
	upcoming_follow_ups: LeadFollowUpItem[];
	overdue_follow_ups: LeadFollowUpItem[];
	monthly_trend: LeadMonthlyTrend[];
	top_clients: LeadTopClient[];
	by_category: LeadCategoryBreakdown[];
	recent_activity: LeadRecentActivity[];
}

export interface LeadFilters {
	"filter[search]"?: string;
	"filter[status]"?: string;
	"filter[category]"?: string;
	"filter[prepared_by]"?: string;
	"filter[priority]"?: string;
	per_page?: number;
	page?: number;
}
